import { Link } from "react-router-dom";
import { Mail } from "lucide-react";
import { Logo } from "./Logo";
import { SmartStoreBadges, StoreQRCodes } from "./StoreBadges";
import { CONTACT_EMAIL, IMAGES } from "@/constants/site";

const cols = [
  {
    title: "Product",
    items: [
      { to: "/", label: "Home" },
      { to: "/about", label: "About" },
      { to: "/#features", label: "Features" },
      { to: "/#music", label: "AI Music Studio" },
      { to: "/#download", label: "Download" },
    ],
  },
  {
    title: "Trust & Safety",
    items: [
      { to: "/safety", label: "Community Guidelines" },
      { to: "/child-safety", label: "Child Safety Standards" },
      { to: "/safety#security", label: "Security" },
      { to: "/privacy", label: "Privacy Policy" },
    ],
  },
  {
    title: "Legal",
    items: [
      { to: "/terms", label: "Terms of Service" },
      { to: "/privacy", label: "Privacy Policy" },
      { to: "/refund", label: "Coins & Refunds" },
      { to: "/support", label: "Support" },
      { to: "/contact", label: "Contact" },
    ],
  },
];

export const Footer = () => {
  return (
    <footer data-testid="site-footer" className="border-t border-black/[0.06] bg-aura-surface">
      <div className="mx-auto max-w-7xl px-6 py-16 md:px-12">
        <div className="grid gap-12 lg:grid-cols-[1.5fr_1fr_1fr_1fr]">
          <div>
            <div className="flex items-center gap-4">
              <img
                src={IMAGES.appIcon}
                alt="AuraLive app icon"
                className="h-14 w-14 rounded-2xl border border-black/10 object-cover"
              />
              <Logo />
            </div>
            <p className="mt-6 max-w-xs text-sm leading-6 text-aura-text2">
              AuraLive is where you go live from real places, discover creators
              on a world map, send gifts and make music with AI — safely.
            </p>
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              data-testid="footer-email"
              className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-aura-ink transition-colors hover:text-aura-pink"
            >
              <Mail size={16} /> {CONTACT_EMAIL}
            </a>

            <div className="mt-7">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-aura-muted">Get the app</p>
              <SmartStoreBadges idPrefix="footer" className="mt-3" />
              <div className="mt-4 w-fit rounded-xl border border-black/[0.06] bg-white p-3">
                <StoreQRCodes testIdPrefix="footer" size={56} />
              </div>
            </div>
          </div>

          {cols.map((c) => (
            <div key={c.title}>
              <h4 className="text-xs font-bold uppercase tracking-[0.2em] text-aura-muted">
                {c.title}
              </h4>
              <ul className="mt-4 space-y-3">
                {c.items.map((it) => (
                  <li key={it.label}>
                    <Link
                      to={it.to}
                      className="text-sm text-aura-text2 transition-colors hover:text-aura-ink"
                    >
                      {it.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-14 flex flex-col items-start justify-between gap-4 border-t border-black/[0.06] pt-6 text-sm text-aura-text2 md:flex-row md:items-center">
          <div className="space-y-1">
            <p>© {new Date().getFullYear()} Mapcast BV. All rights reserved.</p>
            <p className="text-xs text-aura-muted">Herengracht 320, 1016 CE Amsterdam, The Netherlands</p>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs">Verified creators · From your city to the world</span>
          </div>
        </div>
      </div>
    </footer>
  );
};
